import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { trpc, queryClient } from '@/main'
import type { EmployerEntry } from 'shared/types'

// Routes that are only meaningful while a company is the active employer. On the personal
// D'Anchiano profile (companyId === null) the topbar hides their nav items, so a person sitting on
// one of these when they switch back to their own profile would be left on a dead page.
const COMPANY_SCOPED_ROUTE_PREFIXES = ['/teams', '/jobs']

const DANCHIANO_ENTRY: EmployerEntry = {
  companyId: null,
  name: "D'Anchiano",
  image: null,
  employeeType: null,
  reportFormatType: null,
  reevaluate: null,
}

/**
 * Global active-employer context for a person who is an employee of one or more companies.
 * The chosen company is stored in the SESSION (server-side, surfaced as user.activeCompanyId) and
 * applied across every employee-facing view, so the switcher lives globally (topbar), not per page.
 *
 * D'Anchiano is always included as a default option so the employee can switch back to their own
 * profile at any time.
 */
export function useActiveEmployer() {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const router = useRouter()
  const isEmployee = user?.type === 'applicant' && user.isEmployee

  // useSuspenseQuery: getMyEmployers is streamed from the __root loader, so this consumes the streamed
  // data (no client-only fetch, no duplicate request) and SUSPENDS until it's ready — the topbar's
  // local <Suspense> shows its skeleton meanwhile, so the logo/nav never flash wrong values. Safe for
  // anonymous because getMyEmployers is authOptional and returns [] when logged out (no 401 to throw).
  const { data: employers } = useSuspenseQuery(trpc.employees.getMyEmployers.queryOptions())
  const { mutateAsync: setActive } = useMutation(trpc.employees.setActiveEmployer.mutationOptions())

  // Always include D'Anchiano as the first option so the user can switch to their own profile. This
  // list feeds ONLY the switcher dropdown — it may lag the user (it's a separate query), so it is NOT
  // the source of the ACTIVE employer below.
  const companyEmployers: EmployerEntry[] = employers ?? []
  const list: EmployerEntry[] = companyEmployers.length > 0 ? [DANCHIANO_ENTRY, ...companyEmployers] : []
  // The ACTIVE employer comes from getUser (`user.activeEmployer`, resolved server-side from the
  // session's company), so the chrome renders it ATOMICALLY with the user — no waiting on the
  // getMyEmployers list to land, which previously caused a personal→employer two-phase on a session
  // switch. null/undefined = personal D'Anchiano; fall back to `list[0]` (D'Anchiano) when the list is
  // loaded, else undefined (non-employee), preserving the prior shape.
  const activeEmployer = user?.activeEmployer ?? list[0]
  const activeEmployerId = activeEmployer?.companyId ?? null

  const setActiveEmployerId = async (companyId: number | null) => {
    await setActive(companyId)
    // The mutation has committed the new context to the session, so reflect it on the cached user
    // right away. The topbar (logo/name) derives from user.activeEmployer/activeCompanyId — without this
    // it only updates when the getUser refetch below lands, and if that refetch is cancelled or raced the
    // old employer stays on screen until a manual refresh. Update BOTH the id and the resolved entry (the
    // chrome reads activeEmployer) from the already-loaded switcher list.
    const nextEmployer = companyId == null ? null : (list.find((e) => e.companyId === companyId) ?? null)
    queryClient.setQueryData(trpc.auth.getUser.queryKey(), (cachedUser) =>
      cachedUser ? { ...cachedUser, activeCompanyId: companyId, activeEmployer: nextEmployer } : cachedUser,
    )
    // Switching back to the personal profile (companyId === null) hides the company-scoped sections,
    // so redirect off any of those routes to a page that's always valid for an employee.
    if (companyId === null) {
      const pathname = router.state.location.pathname
      if (COMPANY_SCOPED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
        await router.navigate({ to: '/report' })
      }
    }
    // The active employer is stored in the session (not in query keys), and it changes how almost
    // every employee-facing view renders — the report, teams and the profile all show the active
    // company's snapshot of the person. `getUser` (refetched here) carries the authoritative
    // `activeCompanyId` the topbar derives from; the optimistic write above is just for instant
    // feedback. We deliberately DON'T refetch `getMyEmployers`: the employer list is identical across
    // contexts, so keeping it cached avoids needlessly emptying the switcher's data mid-transition.
    // Refetch every employee-facing view (report/teams/profile snapshots change with the active company)
    // EXCEPT two keys: `getMyEmployers` (identical across contexts — refetching it needlessly empties the
    // switcher mid-transition) and `getUser` (the optimistic `setQueryData` above already wrote the
    // authoritative `activeCompanyId` the mutation just committed, so a refetch returns identical data
    // while racing that optimistic write — skip the redundant request).
    const employersKey = trpc.employees.getMyEmployers.queryKey()
    const userKey = trpc.auth.getUser.queryKey()
    const excluded = new Set([JSON.stringify(employersKey[0]), JSON.stringify(userKey[0])])
    await queryClient.invalidateQueries({
      predicate: (query) => !excluded.has(JSON.stringify(query.queryKey[0])),
    })
  }

  return { employers: list, activeEmployer, activeEmployerId, setActiveEmployerId, isEmployee }
}
